import { useCallback, useEffect, useState } from 'react';

/**
 * One value in `sessionStorage`: its key, how to read it back and how to store it.
 *
 * `parse` returns `undefined` for anything it does not recognise, and the slot's
 * `fallback` is used instead. `serialize` returning `null` removes the key.
 */
export type SessionSlot<T> = {
  key: string;
  fallback: T;
  parse: (raw: string) => T | undefined;
  serialize: (value: T) => string | null;
};

const eventName = (key: string) => `session-state:${key}`;

/** The stored value, or the fallback when it is absent, invalid or storage throws. */
export const readSession = <T>(slot: SessionSlot<T>): T => {
  try {
    const raw = sessionStorage.getItem(slot.key);
    return raw === null ? slot.fallback : (slot.parse(raw) ?? slot.fallback);
  } catch {
    return slot.fallback;
  }
};

/**
 * Store a value and tell every hook in this tab that reads the same key.
 *
 * The event carries the value in `detail`, and listeners take it from there
 * rather than re-reading storage: when storage is blocked, a re-read would give
 * the fallback and undo the change that sent the event. So the value still
 * applies on this page; it only does not survive a navigation.
 */
export const writeSession = <T>(slot: SessionSlot<T>, value: T): void => {
  try {
    const serialized = slot.serialize(value);
    if (serialized === null) {
      sessionStorage.removeItem(slot.key);
    } else {
      sessionStorage.setItem(slot.key, serialized);
    }
  } catch {
    // Blocked storage (private browsing can throw). The event below still applies it.
  }
  window.dispatchEvent(new CustomEvent<T>(eventName(slot.key), { detail: value }));
};

/**
 * `useState` backed by a session slot, kept in step across every component in
 * the tab that uses the same slot.
 */
export const useSessionState = <T>(slot: SessionSlot<T>): readonly [T, (next: T) => void] => {
  const [value, setValue] = useState<T>(() => readSession(slot));

  useEffect(() => {
    const onChange = (event: Event) => setValue((event as CustomEvent<T>).detail);
    const name = eventName(slot.key);
    window.addEventListener(name, onChange);
    return () => window.removeEventListener(name, onChange);
  }, [slot.key]);

  const write = useCallback((next: T) => writeSession(slot, next), [slot]);

  return [value, write] as const;
};
